import React, { useEffect, useState, useCallback } from 'react';
import {
   Box,
   Typography,
   Grid,
   Card,
   CardMedia,
   CardActions,
   Button,
   IconButton,
   Chip,
   CircularProgress,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   Snackbar,
   Alert,
} from '@mui/material';
import {
   Download as DownloadIcon,
   Delete as DeleteIcon,
   AutoFixHigh as EnhancedIcon,
   PhotoCamera as CameraIcon,
} from '@mui/icons-material';
import { collection, query, where, orderBy, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useUser } from '../contexts/UserContext';
import { getAuthHeaders } from '../utils/auth';

interface EnhancedPhoto {
   id: string;
   url: string;
   originalUrl?: string;
   createdAt: any;
   basePhotoId?: string;
}

const EnhancedPhotos: React.FC = () => {
   const { currentUser } = useUser();
   const [photos, setPhotos] = useState<EnhancedPhoto[]>([]);
   const [loading, setLoading] = useState(true);
   const [deleteTarget, setDeleteTarget] = useState<EnhancedPhoto | null>(null);
   const [deleting, setDeleting] = useState(false);
   const [snackMsg, setSnackMsg] = useState('');
   const [snackSeverity, setSnackSeverity] = useState<'success' | 'error'>('success');
   const [snackOpen, setSnackOpen] = useState(false);

   const fetchPhotos = useCallback(async () => {
      if (!currentUser?.uid) return;
      setLoading(true);
      try {
         const q = query(
            collection(db, 'photos'),
            where('userId', '==', currentUser.uid),
            where('isEnhanced', '==', true),
            orderBy('createdAt', 'desc')
         );
         const snap = await getDocs(q);
         setPhotos(snap.docs.map((d) => ({ id: d.id, ...d.data() } as EnhancedPhoto)));
      } catch (err) {
         console.error('Failed to fetch enhanced photos:', err);
      } finally {
         setLoading(false);
      }
   }, [currentUser?.uid]);

   useEffect(() => {
      fetchPhotos();
   }, [fetchPhotos]);

   const handleDownload = async (photo: EnhancedPhoto) => {
      try {
         const proxyUrl = `${process.env.REACT_APP_API_URL}/api/photos/download-proxy?url=${encodeURIComponent(photo.url)}`;
         const headers = await getAuthHeaders();
         const response = await fetch(proxyUrl, {
            headers: { Authorization: headers.Authorization },
         });
         if (!response.ok) throw new Error('Download failed');
         const blob = await response.blob();
         const blobUrl = window.URL.createObjectURL(blob);
         const a = document.createElement('a');
         a.href = blobUrl;
         a.download = `enhanced_${Date.now()}.jpg`;
         document.body.appendChild(a);
         a.click();
         document.body.removeChild(a);
         window.URL.revokeObjectURL(blobUrl);
      } catch {
         setSnackSeverity('error');
         setSnackMsg('Failed to download photo');
         setSnackOpen(true);
      }
   };

   const handleDelete = async () => {
      if (!deleteTarget) return;
      setDeleting(true);
      try {
         await deleteDoc(doc(db, 'photos', deleteTarget.id));
         setPhotos((prev) => prev.filter((p) => p.id !== deleteTarget.id));
         setSnackSeverity('success');
         setSnackMsg('Photo deleted');
      } catch {
         setSnackSeverity('error');
         setSnackMsg('Failed to delete photo');
      } finally {
         setDeleting(false);
         setDeleteTarget(null);
         setSnackOpen(true);
      }
   };

   if (loading) {
      return (
         <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#1D9E75' }} />
         </Box>
      );
   }

   if (photos.length === 0) {
      return (
         <Box
            sx={{
               display: 'flex',
               flexDirection: 'column',
               alignItems: 'center',
               py: 8,
               px: 2,
            }}
         >
            <CameraIcon sx={{ fontSize: 64, color: '#546E7A', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#B0BEC5', mb: 1 }}>
               No enhanced photos yet
            </Typography>
            <Typography variant="body2" sx={{ color: '#546E7A', textAlign: 'center' }}>
               Visit AI Studio to enhance your photos. Saved enhancements will appear here.
            </Typography>
         </Box>
      );
   }

   return (
      <Box>
         <Typography variant="body2" sx={{ color: '#546E7A', mb: 2 }}>
            {photos.length} enhanced photo{photos.length !== 1 ? 's' : ''}
         </Typography>

         <Grid container spacing={2}>
            {photos.map((photo) => (
               <Grid item xs={6} sm={4} md={3} key={photo.id}>
                  <Card
                     sx={{
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'divider',
                        position: 'relative',
                     }}
                  >
                     <Chip
                        icon={<EnhancedIcon sx={{ fontSize: 14 }} />}
                        label="Enhanced"
                        size="small"
                        sx={{
                           position: 'absolute',
                           top: 8,
                           left: 8,
                           zIndex: 2,
                           background: 'rgba(29,158,117,0.85)',
                           color: '#fff',
                           fontSize: '11px',
                           height: 24,
                           backdropFilter: 'blur(4px)',
                        }}
                     />
                     <CardMedia
                        component="img"
                        height={180}
                        image={photo.url}
                        alt="Enhanced photo"
                        sx={{ objectFit: 'cover', imageOrientation: 'from-image' }}
                     />
                     <CardActions sx={{ justifyContent: 'space-between', px: 1, py: 0.5 }}>
                        <Typography variant="caption" sx={{ color: '#546E7A' }}>
                           {photo.createdAt?.toDate
                              ? photo.createdAt.toDate().toLocaleDateString()
                              : photo.createdAt
                                ? new Date(photo.createdAt).toLocaleDateString()
                                : ''}
                        </Typography>
                        <Box>
                           <IconButton size="small" onClick={() => handleDownload(photo)} title="Download">
                              <DownloadIcon sx={{ fontSize: 18 }} />
                           </IconButton>
                           <IconButton
                              size="small"
                              onClick={() => setDeleteTarget(photo)}
                              title="Delete"
                              sx={{ color: '#ef5350' }}
                           >
                              <DeleteIcon sx={{ fontSize: 18 }} />
                           </IconButton>
                        </Box>
                     </CardActions>
                  </Card>
               </Grid>
            ))}
         </Grid>

         <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
            <DialogTitle>Delete Enhanced Photo</DialogTitle>
            <DialogContent>
               <Typography>Are you sure you want to delete this enhanced photo? This cannot be undone.</Typography>
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
                  Cancel
               </Button>
               <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
                  {deleting ? 'Deleting...' : 'Delete'}
               </Button>
            </DialogActions>
         </Dialog>

         <Snackbar
            open={snackOpen}
            autoHideDuration={3000}
            onClose={() => setSnackOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
         >
            <Alert onClose={() => setSnackOpen(false)} severity={snackSeverity}>
               {snackMsg}
            </Alert>
         </Snackbar>
      </Box>
   );
};

export default EnhancedPhotos;
